import { ServiceError, ServiceResponse } from "@securityjourney/service-client";
import {
  components as contentComponents,
  paths,
} from "@securityjourney/svc-content/src/gen/content_api";

import {
  components as catalogComponents,
  paths as catalogPaths,
} from "@securityjourney/svc-content/src/gen/catalog";
import { OpenApiFetchWrapper } from "./openApiFetchWrapper";

import {
  components as logComponents,
  paths as logPaths,
} from "@securityjourney/svc-content/src/gen/logs";

// Base tab properties common to all tab types
type BaseSandboxTab = {
  title: string;
  iconClass?: string;
};

// Individual SandboxTab variants
export type AppTab = BaseSandboxTab & {
  app: contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox.Tab.App"];
  type: "app";
};

export type EditorTab = BaseSandboxTab & {
  editor: contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox.Tab.Editor"];
  type: "editor";
};

export type InterceptTab = BaseSandboxTab & {
  intercept: contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox.Tab.Intercept"];
  type: "intercept";
};

export type TestsTab = BaseSandboxTab & {
  tests: contentComponents["schemas"]["securityjourney.proto.content.TestsConfiguration"];
  type: "tests";
};

export type UnknownTab = BaseSandboxTab & {
  type: "unknown";
};

// Union type of all possible tab variants
export type SandboxTab = AppTab | EditorTab | InterceptTab | TestsTab | UnknownTab;

// The generated TagOption marks every proto3 field optional, but svc-content always
// populates id and displayName, so restate the actual contract for consumers.
export type CatalogTagOption = {
  id: string;
  displayName: string;
  description?: string | null;
};

export type CatalogCollection =
  catalogComponents["schemas"]["securityjourney.proto.content.Collection"];

export type CatalogLesson = catalogComponents["schemas"]["securityjourney.proto.content.Lesson"];

export type SandboxLessonDetails = Omit<
  contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2"],
  "sandbox"
> & {
  lessonType?: string;
  completionType: "tests" | "flagSubmission" | "steps" | "quiz";
  hasCodeTests: boolean;
  isCtf: boolean;
  hasFinishButton: boolean;
  engines: string[];
  tests?: contentComponents["schemas"]["securityjourney.proto.content.TestsConfiguration"] | null;
  sandbox: Omit<
    contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox"],
    "tabs"
  > & {
    tabs: SandboxTab[];
  };
  since?: string;
};

export type VideoLessonDetails = {
  lessonKey: string;
  lessonVersion: number;
  title: string;
  description?: string;
  completionType: "quiz";
  videoId: string;
};

export type LessonInstructions =
  contentComponents["schemas"]["securityjourney.proto.content.Instructions"];
export type HintDetails = contentComponents["schemas"]["securityjourney.proto.content.Hint"];
export type StepDetails =
  contentComponents["schemas"]["securityjourney.proto.content.Instructions.Step"];
export type TestConfiguration =
  contentComponents["schemas"]["securityjourney.proto.content.TestsConfiguration"];
export type TestCase = contentComponents["schemas"]["securityjourney.proto.content.TestCase"];
export type ReplParam = {
  "repl-engine": string;
};

export type CatalogCollectionParams = {
  selectedTenant?: string;
  name?: string;
  search?: string;
  description?: string;
  tags?: string[];
  lessonTypes?: catalogComponents["schemas"]["securityjourney.proto.content.LessonConfigurationType"][];
  recentlyReleased?: boolean;
  recentlyRefreshed?: boolean;
  levelId?: number;
  type?: catalogComponents["schemas"]["securityjourney.proto.content.LessonConfigurationType"];
  hasTests?: boolean;
  customCollections?: boolean;
  collectionContentTypes?: string[];
  searchCorrelationId?: string;
};

export type CatalogLessonParams = {
  selectedTenant?: string;
  name?: string;
  search?: string;
  description?: string;
  type?: string[];
  availability?: catalogComponents["schemas"]["securityjourney.proto.content.Availability"];
  hasTest?: boolean;
  tags?: string[];
  sortBy?: catalogComponents["schemas"]["securityjourney.proto.content.SortBy"];
  /** @description Sort order (ascending or descending). Default asc. */
  sortOrder?: catalogComponents["schemas"]["securityjourney.proto.content.SortOrder"];
  recentlyReleased?: boolean;
  recentlyRefreshed?: boolean;
  searchCorrelationId?: string;
};

export type LessonDetailsPublic = {
  readonly lessonKey?: string;
  readonly lessonName?: string;
  readonly lessonDescription?: string;
  readonly lessonType?: contentComponents["schemas"]["securityjourney.proto.content.LessonConfigurationType"];
  readonly lessonConfiguration?: contentComponents["schemas"]["google.protobuf.Struct"];
};

export type LogsResponse =
  logComponents["schemas"]["securityjourney.proto.content.SearchLogsPublicResponse"];

export type TaxonomyRequest = {
  topics: string[];
};

export type LessonsByTopicResponse =
  contentComponents["schemas"]["securityjourney.proto.content.GetLessonsByTopicResponse"];

export class ContentClient extends OpenApiFetchWrapper<paths & catalogPaths & logPaths> {
  constructor() {
    super();
  }

  async getLessonDetails(lessonKey: string): Promise<SandboxLessonDetails> {
    const { data, error } = await this.get("/svc/content/lesson/{lessonKey}/v/{lessonVersion}", {
      params: { path: { lessonKey, lessonVersion: 1 } },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data) {
      throw new ServiceError(new Error("No lesson details returned"));
    }

    // Pull the choices from the repl-engine parameter and put it at the top level of the lesson details
    const replEngineParam = data.sandbox?.parameters?.find(p => p.key === "repl-engine");
    const choices = replEngineParam?.choices;

    // Pull the tests cases from the test tab if present
    const testTab = data.sandbox?.tabs.find(t => "tests" in t && t.tests);
    const tests = testTab && "tests" in testTab ? (testTab.tests ?? null) : null;

    // Add type property to each tab based on its content
    const tabs: SandboxTab[] =
      data.sandbox?.tabs
        .map(tab => {
          if ("app" in tab && tab.app) {
            return {
              ...tab,
              app: tab.app,
              type: "app",
            } as AppTab;
          } else if ("intercept" in tab && tab.intercept) {
            return {
              ...tab,
              intercept: tab.intercept,
              type: "intercept",
            } as InterceptTab;
          } else if ("editor" in tab && tab.editor) {
            return {
              ...tab,
              editor: tab.editor,
              type: "editor",
            } as EditorTab;
          } else if ("tests" in tab && tab.tests) {
            return {
              ...tab,
              tests: tab.tests,
              type: "tests",
            } as TestsTab;
          } else {
            return {
              ...tab,
              type: "unknown",
            } as UnknownTab;
          }
        })
        .filter(tab => {
          // Filter out editor tabs if replEngineParam is not present
          return replEngineParam || tab.type !== "editor";
        }) || [];

    // Pull out some properties of the lesson sandbox for easier access
    const hasCodeTests = tests?.kind === "CodeSubmission";
    const isCtf = data.sandbox?.instructions.requiresFlagSubmission || false;

    // If this sandbox doesn't have tests and isn't CTF, then we'll need a "finish" button
    const hasFinishButton = !tests && !isCtf;

    // Determine completion type
    const completionType: SandboxLessonDetails["completionType"] = tests
      ? "tests"
      : isCtf
        ? "flagSubmission"
        : "steps";

    // Return the lesson details with added engines, tests, and typed tabs
    const returnDetails: SandboxLessonDetails = {
      ...data,
      completionType,
      hasCodeTests,
      isCtf,
      hasFinishButton,
      sandbox: {
        ...data.sandbox!,
        tabs,
      },
      engines: choices ?? [],
      tests,
    };
    return returnDetails;
  }

  async getTags(): Promise<Record<string, string[]>> {
    const { data, error } = await this.get("/svc/content/catalog/tags");

    if (error) {
      throw new ServiceError(error);
    }

    if (!data.tags) {
      throw new ServiceError(new Error("No tags returned"));
    }

    return Object.fromEntries(
      Object.entries(data.tags).map(([key, value]) => [key, value?.values ?? []])
    );
  }

  async getCollections(
    params: CatalogCollectionParams = {}
  ): Promise<catalogComponents["schemas"]["securityjourney.proto.content.Collection"][]> {
    const { data, error } = await this.get("/svc/content/catalog/collections", {
      params: { query: params },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data?.collections) {
      throw new ServiceError(new Error("No collections returned"));
    }

    return data.collections;
  }

  async getLessons(
    params: CatalogLessonParams = {}
  ): Promise<catalogComponents["schemas"]["securityjourney.proto.content.Lesson"][]> {
    const { data, error } = await this.get("/svc/content/catalog/lessons", {
      params: { query: params },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data?.lessons) {
      throw new ServiceError(new Error("No lessons returned"));
    }
    return data.lessons;
  }

  async getLessonPublic(
    lessonKey: string,
    selectedTenant?: string | null
  ): Promise<ServiceResponse<LessonDetailsPublic>> {
    const { data, error } = await this.get("/svc/content/lesson/v2/{lessonKey}", {
      params: {
        path: { lessonKey },
        ...(selectedTenant ? { query: { selectedTenant } } : {}),
      },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data) {
      throw new ServiceError(new Error("No lesson details returned"));
    }

    return { data, error };
  }

  async getLessonsPublic(
    lessonKeys: string[],
    selectedTenant?: string | null
  ): Promise<LessonDetailsPublic[]> {
    const { data, error } = await this.post("/svc/content/lessons/v2/public", {
      body: { lessonKeys, selectedTenant },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data) {
      throw new ServiceError(new Error("No lesson details returned"));
    }

    return data.lessons ?? [];
  }

  async fetchSearchInsights(
    selectedTenant: string,
    since: Date,
    limit = 10
  ): Promise<LogsResponse> {
    const { data, error } = await this.get("/svc/content/catalog/logs", {
      params: { query: { selectedTenant, since: since.toISOString(), topQueriesLimit: limit } },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data) {
      throw new ServiceError(new Error("No log data returned"));
    }

    return data;
  }

  async fetchTaxonomy(topics: string[], selectedTenant: string): Promise<LessonsByTopicResponse> {
    const { data, error } = await this.post("/svc/content/lessons/by-topic", {
      body: { topics, selectedTenant },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data) {
      throw new ServiceError(new Error("No taxonomy data returned"));
    }

    return data;
  }

  async getTagsByTenant(selectedTenant: string): Promise<Record<string, CatalogTagOption[]>> {
    const { data, error } = await this.get("/svc/content/catalog/tags/{selectedTenant}", {
      params: { path: { selectedTenant } },
    });

    if (error) {
      throw new ServiceError(error);
    }

    if (!data?.tags) {
      throw new ServiceError(new Error("No tags returned"));
    }

    return Object.fromEntries(
      // Asserts the CatalogTagOption contract (id/displayName always set) over the
      // all-optional generated TagOption shape.
      Object.entries(data.tags).map(([key, value]) => [
        key,
        (value?.values ?? []) as CatalogTagOption[],
      ])
    );
  }
}

let lessonContentClientInstance: ContentClient | null = null;

export const getContentClient = (): ContentClient => {
  if (!lessonContentClientInstance) {
    lessonContentClientInstance = new ContentClient();
  }
  return lessonContentClientInstance;
};
